<template>
    <common-page-plain class="offer-filter">
        <div class="offer-filter__filter">
            <div class="offer-filter__sort">
                <span class="offer-filter__sort--btn" @click.stop="openFilterDropdown()">
                    <common-icon name="filter" size="huge"></common-icon>
                    {{ $t('forms.offer.offer_filter.filter') }}
                </span>
                <span class="offer-filter__sort--span">
                    <div class="offer-filter__sort--btn offer-filter__sort--btn-sort" @click="togleSortDropdown">
                        <common-icon name="sort" size="large"></common-icon>
                        {{ $t(`forms.offer.offer_filter.sort_options.${sortLabel}`) }}
                    </div>
                    <common-dropdown
                        v-click-away="closeSortDropdown"
                        class="offer-filter__sort--dropdown"
                        :isOpen="isSortDropdownOpen"
                        :recalculatePosition="true"
                        :hasArrow="false"
                        arrowPositionX="0"
                        arrowPositionY="0"
                    >
                        <nav class="dropdown__menu">
                            <button v-for="sortOpt in sortOptions" :key="sortOpt" @click="sortClick(sortOpt)">
                                {{ $t(`forms.offer.offer_filter.sort_options.${sortOpt.label}`) }}
                            </button>
                        </nav>
                    </common-dropdown>
                </span>
            </div>
        </div>
        <div v-if="filterBadges.length || showMapToggle" class="offer-filter__badges">
            <div class="offer-filter__badges--div">
                <common-badge
                    v-for="badge in filterBadges"
                    :key="badge.key"
                    :title="badge.title"
                    :value="badge.value"
                    icon="close"
                    @badge-click="removeFilter"
                ></common-badge>
            </div>
            <checkbox-input
                v-if="showMapToggle"
                class="offer-filter__badges--map-switch"
                :label="$t('pages.offer.find_offers.view_map')"
                :value="isMapOpen"
                type="switch"
                labelPosition="left"
                @change="toggleMap()"
            ></checkbox-input>
        </div>
    </common-page-plain>
</template>

<script>
    import OfferSortFilterEnum from 'enums/offer_sort_filter.enum'
    import cloneDeep from 'lodash.clonedeep'
    import ServiceCategoryAPI from 'api/ServiceCategoryAPI'
    import RoutesEnum from 'enums/routes.enum'
    import LanguageEnum from 'enums/language.enum'
    import Store from '@/store'
    import CheckboxInput from 'components/form-inputs/CheckboxInput.vue'

    export default {
        components: { CheckboxInput },

        props: {
            filter: { type: Object, required: true },
            showMapToggle: { type: Boolean, default: false },
        },

        data: () => ({
            allCategories: [],
            isSortDropdownOpen: false,
            sortLabel: 'sort',
            cityOptions: [],
            isMapOpen: true,
            isLocationAllowed: true,
        }),

        computed: {
            sortOptions() {
                const arrayData = Object.values(OfferSortFilterEnum).map((status) => ({
                    label: status.label,
                    status,
                    value: status.value,
                }))
                if (!this.isLocationAllowed) {
                    return arrayData.filter((item) => item.value.orderBy !== OfferSortFilterEnum.distance_ASC.value.orderBy)
                }
                return arrayData
            },
            badgeDivClass() {
                return {
                    badge: true,
                    'offer-filter__badges--div': true,
                    'offer-filter__badges--div-notoggle': this.showMapToggle,
                }
            },
            filterBadges() {
                const filter = cloneDeep(this.filter)
                delete filter.includeDescription
                delete filter.currency

                if (filter.orderBy && filter.wayDefinition) {
                    filter.orderBy = `${filter.orderBy}_${filter.wayDefinition}`
                }
                delete filter.wayDefinition

                if (filter.serviceId) {
                    delete filter.serviceGroupId
                }

                return Object.keys(filter).map((key) => ({ title: this.getBadgeTitle(key, filter[key]), value: key }))
            },
        },

        methods: {
            toggleMap() {
                this.isMapOpen = !this.isMapOpen
                this.emitEvent({ name: 'togglemap' })
            },
            getBadgeTitle(key, val) {
                let value = val
                if (key === 'fromPrice' || key === 'toPrice') {
                    value = `${val}  ${this.filter.currency}`
                } else if (key === 'countryId') {
                    const selectedCountry = Store.getters['data/getCountries'].find((country) => country.id === Number(val))
                    this.fetchCities(selectedCountry.name)
                    value = this.$t(
                        `forms.company.create_company.fields.country.${selectedCountry.name.split(' ')[0].toUpperCase()}`,
                    )
                } else if (key === 'cityId') {
                    value = this.cityOptions?.find((city) => city.value === Number(val))?.label
                } else if (key === 'serviceId') {
                    this.allCategories.forEach((category) => {
                        const service = category.services.find((service) => service.id === Number(val))
                        if (service) {
                            value =
                                this.$i18n.locale === LanguageEnum.EN
                                    ? service.name
                                    : service[`name${this.$i18n.locale.toUpperCase()}`]
                            return
                        }
                    })
                } else if (key === 'serviceGroupId') {
                    const category = this.allCategories.find((category) => category.id === Number(val))
                    if (category) {
                        value =
                            this.$i18n.locale === LanguageEnum.EN
                                ? category.name
                                : category[`name${this.$i18n.locale.toUpperCase()}`]
                    }
                } else if (key === 'orderBy') {
                    return `${this.$t(`components.offer.offer_filter.badges.${key}`)}: ${this.$t(
                        `components.offer.offer_filter.badges.sort.${value}`,
                    )}`
                }
                return `${this.$t(`components.offer.offer_filter.badges.${key}`)}: ${value}`
            },

            fetchCities(selectedCountry) {
                this.cityOptions = Store.getters['data/getCountries']
                    .find((country) => country.name === selectedCountry)
                    .cities.map((city) => ({
                        value: city.id,
                        label: city.name,
                    }))
            },

            fetchServiceCategories() {
                return ServiceCategoryAPI.getAll().then((categories) => {
                    this.allCategories = categories
                })
            },

            removeFilter(option) {
                const query = cloneDeep(this.$route.query)
                delete query[option]
                if (option === 'orderBy') {
                    delete query['wayDefinition']
                }
                if (option === 'serviceId') {
                    delete query['serviceGroupId']
                }
                if (option === 'orderBy') {
                    this.sortLabel = 'sort'
                }
                this.$router.push({ name: RoutesEnum.OFFER.FIND, query })
            },

            togleSortDropdown() {
                this.isSortDropdownOpen = !this.isSortDropdownOpen
            },

            closeSortDropdown() {
                this.isSortDropdownOpen = false
            },

            sortClick(sortOpt) {
                this.closeSortDropdown()
                this.sortLabel = sortOpt.label
                const query = Object.assign({}, cloneDeep(this.$route.query), sortOpt.value)
                this.$router.push({ name: RoutesEnum.OFFER.FIND, query })
            },

            getSortLabel() {
                const { orderBy, wayDefinition } = this.$route.query
                if (orderBy && wayDefinition) {
                    this.sortLabel = `${orderBy}_${wayDefinition}`
                }
            },

            openFilterDropdown() {
                Store.dispatch('configuration/setIsFilterModalOpen', true)
            },
        },

        mounted() {
            navigator.permissions
                .query({ name: 'geolocation' })
                .then((permissionStatus) => {
                    this.isLocationAllowed = permissionStatus.state === 'granted'
                })
                .catch(() => {
                    this.isLocationAllowed = false
                })
            this.fetchServiceCategories()
            this.getSortLabel()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/offers/_offer-filter.scss';
</style>
